import { z } from 'zod'

/**
 * Lembretes (sprint-7-spec §1): enums e funções puras de agenda. O horário local é calculado por
 * `Intl` (nunca por deslocamento fixo), então horário de verão, se voltar, já é respeitado.
 */

export const DEFAULT_TIME_ZONE = 'America/Sao_Paulo'

export const REMINDER_RECURRENCES = ['none', 'daily', 'weekly', 'monthly'] as const
export type ReminderRecurrence = (typeof REMINDER_RECURRENCES)[number]
export const ReminderRecurrenceSchema = z.enum(REMINDER_RECURRENCES, {
  error: 'Escolha uma recorrência válida.',
})

export const REMINDER_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const
export type ReminderPriority = (typeof REMINDER_PRIORITIES)[number]
export const ReminderPrioritySchema = z.enum(REMINDER_PRIORITIES, {
  error: 'Escolha uma prioridade válida.',
})

export const REMINDER_STATUSES = ['open', 'done', 'cancelled'] as const
export type ReminderStatus = (typeof REMINDER_STATUSES)[number]
export const ReminderStatusSchema = z.enum(REMINDER_STATUSES)

export const OCCURRENCE_STATUSES = ['pending', 'fired', 'done', 'skipped'] as const
export type OccurrenceStatus = (typeof OCCURRENCE_STATUSES)[number]
export const OccurrenceStatusSchema = z.enum(OCCURRENCE_STATUSES)

export const REMINDER_PRIORITY_LABELS: Readonly<Record<ReminderPriority, string>> = Object.freeze({
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
})

export const REMINDER_RECURRENCE_LABELS: Readonly<Record<ReminderRecurrence, string>> =
  Object.freeze({ none: 'Única', daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal' })

/** Severidade da notificação de um lembrete pela prioridade (`reminders:tick`). */
export function reminderSeverity(priority: ReminderPriority): 'info' | 'attention' | 'critical' {
  if (priority === 'urgent') return 'critical'
  if (priority === 'high') return 'attention'
  return 'info'
}

// --- Fuso horário --------------------------------------------------------------------------------

export interface ZonedParts {
  year: number
  /** 1–12 */
  month: number
  day: number
  hour: number
  minute: number
  second: number
  /** 0 = domingo … 6 = sábado */
  weekday: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()
function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    })
    formatters.set(timeZone, formatter)
  }
  return formatter
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

/** Partes da data e hora de `date` no fuso `timeZone`. */
export function zonedParts(date: Date, timeZone: string = DEFAULT_TIME_ZONE): ZonedParts {
  const parts: Record<string, string> = {}
  for (const part of formatterFor(timeZone).formatToParts(date)) parts[part.type] = part.value
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAYS[parts.weekday ?? 'Sun'] ?? 0,
  }
}

function offsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - (date.getTime() - date.getUTCMilliseconds())
}

/** Instante da data e hora local (`month` de 1 a 12) no fuso. Hora inexistente avança. */
export function zonedTimeToUtc(
  local: {
    year: number
    month: number
    day: number
    hour?: number
    minute?: number
    second?: number
  },
  timeZone: string = DEFAULT_TIME_ZONE,
): Date {
  const guess = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour ?? 0,
    local.minute ?? 0,
    local.second ?? 0,
  )
  const first = guess - offsetMs(new Date(guess), timeZone)
  const second = guess - offsetMs(new Date(first), timeZone)
  return new Date(second)
}

const pad = (value: number, size = 2) => String(value).padStart(size, '0')

/** Data local `YYYY-MM-DD` de `date` no fuso. */
export function localDateIn(date: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
  const p = zonedParts(date, timeZone)
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`
}

/** Hora local `HH:mm` de `date` no fuso. */
export function localTimeIn(date: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
  const p = zonedParts(date, timeZone)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** Início (inclusivo) e fim (exclusivo) do dia local de `date`, como instantes. */
export function localDayBounds(
  date: Date,
  timeZone: string = DEFAULT_TIME_ZONE,
): { start: Date; end: Date } {
  const p = zonedParts(date, timeZone)
  const next = addDays(p.year, p.month, p.day, 1)
  return {
    start: zonedTimeToUtc({ year: p.year, month: p.month, day: p.day }, timeZone),
    end: zonedTimeToUtc(next, timeZone),
  }
}

/** Último dia do mês (`month` de 1 a 12). */
export function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function addDays(year: number, month: number, day: number, days: number) {
  const d = new Date(Date.UTC(year, month - 1, day + days))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
}

function weekdayOf(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

function dateKey(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`
}

// --- Recorrência ---------------------------------------------------------------------------------

export interface ReminderSchedule {
  /** Primeira data e hora (define também o horário local de todas as ocorrências). */
  dueAt: Date | string
  recurrence: ReminderRecurrence
  /** 0–6, só `weekly`. Vazio = o dia da semana de `dueAt`. */
  weekdays?: readonly number[] | null
  /** 1–31, só `monthly`. Nulo = o dia de `dueAt`. 29/30/31 em mês mais curto = último dia. */
  dayOfMonth?: number | null
  /** Último dia (local, `YYYY-MM-DD`, inclusivo) em que pode haver ocorrência. */
  until?: string | Date | null
}

const toDate = (value: Date | string) => (value instanceof Date ? value : new Date(value))

function untilKey(until: string | Date | null | undefined): string | null {
  if (until === null || until === undefined) return null
  if (until instanceof Date) return until.toISOString().slice(0, 10)
  return until.slice(0, 10)
}

/** Limite de busca: pouco mais de 4 anos de dias (cobre qualquer combinação válida). */
const MAX_SCAN_DAYS = 1500

/**
 * Próxima ocorrência ≥ `after`. `none` → `dueAt` se ≥ `after`, senão `null`. Recorrentes começam
 * em `dueAt` (nunca antes) e param em `until`; o horário local de `dueAt` vale para todas.
 */
export function nextOccurrence(
  reminder: ReminderSchedule,
  after: Date,
  timeZone: string = DEFAULT_TIME_ZONE,
): Date | null {
  const dueAt = toDate(reminder.dueAt)
  if (reminder.recurrence === 'none') {
    return dueAt.getTime() >= after.getTime() ? dueAt : null
  }
  const start = zonedParts(dueAt, timeZone)
  const limit = untilKey(reminder.until)
  const from = dueAt.getTime() > after.getTime() ? dueAt : after
  const fromLocal = zonedParts(from, timeZone)
  const weekdays =
    reminder.weekdays && reminder.weekdays.length > 0 ? reminder.weekdays : [start.weekday]
  const wantedDay = reminder.dayOfMonth ?? start.day

  for (let i = 0; i <= MAX_SCAN_DAYS; i += 1) {
    const d = addDays(fromLocal.year, fromLocal.month, fromLocal.day, i)
    const key = dateKey(d.year, d.month, d.day)
    if (limit !== null && key > limit) return null
    let matches = true
    if (reminder.recurrence === 'weekly') {
      matches = weekdays.includes(weekdayOf(d.year, d.month, d.day))
    } else if (reminder.recurrence === 'monthly') {
      matches = d.day === Math.min(wantedDay, lastDayOfMonth(d.year, d.month))
    }
    if (!matches) continue
    const candidate = zonedTimeToUtc(
      { ...d, hour: start.hour, minute: start.minute, second: start.second },
      timeZone,
    )
    candidate.setTime(candidate.getTime() + dueAt.getUTCMilliseconds())
    if (candidate.getTime() >= from.getTime()) return candidate
  }
  return null
}

/**
 * Total de ocorrências de um recorrente com `until` ("3 de 10"); `null` quando não tem fim (∞).
 * Para `none`, 1. Conta no máximo `cap`.
 */
export function countOccurrences(
  reminder: ReminderSchedule,
  timeZone: string = DEFAULT_TIME_ZONE,
  cap = 1000,
): number | null {
  if (reminder.recurrence === 'none') return 1
  if (reminder.until === null || reminder.until === undefined) return null
  let count = 0
  let cursor: Date | null = nextOccurrence(reminder, toDate(reminder.dueAt), timeZone)
  while (cursor && count < cap) {
    count += 1
    cursor = nextOccurrence(reminder, new Date(cursor.getTime() + 1000), timeZone)
  }
  return count
}

// --- Adiar ---------------------------------------------------------------------------------------

export const SNOOZE_TARGET_KEYS = ['in_1h', 'tomorrow', 'next_monday', 'custom'] as const
export type SnoozeTargetKey = (typeof SNOOZE_TARGET_KEYS)[number]

export interface SnoozeTarget {
  key: SnoozeTargetKey
  label: string
  /** Nulo em "Escolher…" (a pessoa escolhe data e hora). */
  at: Date | null
}

/** Hora local das opções "Amanhã" e "Próxima segunda" do adiar (texto da spec §1). */
export const SNOOZE_MORNING_HOUR = 9

/** Opções do adiar: "+1 h", "Amanhã às 09:00", "Próxima segunda às 09:00", "Escolher…". */
export function snoozeTargets(now: Date, timeZone: string = DEFAULT_TIME_ZONE): SnoozeTarget[] {
  const today = zonedParts(now, timeZone)
  const tomorrow = addDays(today.year, today.month, today.day, 1)
  const toMonday = (8 - today.weekday) % 7 || 7
  const monday = addDays(today.year, today.month, today.day, toMonday)
  const hh = `${pad(SNOOZE_MORNING_HOUR)}:00`
  return [
    { key: 'in_1h', label: '+1 h', at: new Date(now.getTime() + 3_600_000) },
    {
      key: 'tomorrow',
      label: `Amanhã às ${hh}`,
      at: zonedTimeToUtc({ ...tomorrow, hour: SNOOZE_MORNING_HOUR }, timeZone),
    },
    {
      key: 'next_monday',
      label: `Próxima segunda às ${hh}`,
      at: zonedTimeToUtc({ ...monday, hour: SNOOZE_MORNING_HOUR }, timeZone),
    },
    { key: 'custom', label: 'Escolher…', at: null },
  ]
}

/** Limites do adiar (spec §2): de agora + 5 min a agora + 30 dias. */
export const SNOOZE_MIN_MS = 5 * 60_000
export const SNOOZE_MAX_MS = 30 * 86_400_000

export const SNOOZE_MESSAGES = {
  tooSoon: 'Adie para pelo menos 5 minutos a partir de agora.',
  tooFar: 'Adie para no máximo 30 dias a partir de agora.',
} as const

/** Mensagem de erro do adiar para `until`, ou `null` quando vale. */
export function snoozeUntilError(until: Date, now: Date): string | null {
  const delta = until.getTime() - now.getTime()
  if (delta < SNOOZE_MIN_MS) return SNOOZE_MESSAGES.tooSoon
  if (delta > SNOOZE_MAX_MS) return SNOOZE_MESSAGES.tooFar
  return null
}

// --- Validação de forma (criar e editar depois de mesclar) ---------------------------------------

export const REMINDER_MESSAGES = {
  title: 'O título precisa ter de 3 a 120 caracteres.',
  description: 'A descrição pode ter no máximo 2.000 caracteres.',
  dueAt: 'Informe data e hora válidas.',
  dueAtPast: 'A data e hora já passaram. Escolha um horário futuro ou uma recorrência.',
  weekdays: 'Escolha ao menos um dia da semana.',
  weekdaysOnlyWeekly: 'Dias da semana só valem para a recorrência semanal.',
  weekday: 'Use dias da semana de 0 (domingo) a 6 (sábado).',
  dayOfMonth: 'Use um dia do mês de 1 a 31.',
  dayOfMonthOnlyMonthly: 'O dia do mês só vale para a recorrência mensal.',
  untilOnlyRecurring: 'A data final só vale para lembretes recorrentes.',
  untilBeforeDue: 'A data final precisa ser igual ou depois da primeira data.',
  reason: 'O motivo pode ter no máximo 500 caracteres.',
  nothingToUpdate: 'Informe ao menos um campo para atualizar.',
  leadWithCustomer: 'Um lembrete de lead não se liga a cliente nem a caso.',
} as const

export interface ReminderShapeIssue {
  path: string
  message: string
}

/** Regras cruzadas de §1 sobre os campos já mesclados (o `dueAt` no passado depende de `now`). */
export function reminderShapeIssues(
  value: ReminderSchedule,
  now?: Date,
  timeZone: string = DEFAULT_TIME_ZONE,
): ReminderShapeIssue[] {
  const issues: ReminderShapeIssue[] = []
  const weekdays = value.weekdays ?? []
  if (value.recurrence === 'weekly' && weekdays.length === 0) {
    issues.push({ path: 'weekdays', message: REMINDER_MESSAGES.weekdays })
  }
  if (value.recurrence !== 'weekly' && weekdays.length > 0) {
    issues.push({ path: 'weekdays', message: REMINDER_MESSAGES.weekdaysOnlyWeekly })
  }
  if (
    value.recurrence !== 'monthly' &&
    value.dayOfMonth !== null &&
    value.dayOfMonth !== undefined
  ) {
    issues.push({ path: 'dayOfMonth', message: REMINDER_MESSAGES.dayOfMonthOnlyMonthly })
  }
  const until = untilKey(value.until)
  if (until !== null) {
    if (value.recurrence === 'none') {
      issues.push({ path: 'until', message: REMINDER_MESSAGES.untilOnlyRecurring })
    } else if (until < localDateIn(toDate(value.dueAt), timeZone)) {
      issues.push({ path: 'until', message: REMINDER_MESSAGES.untilBeforeDue })
    }
  }
  if (now && value.recurrence === 'none' && toDate(value.dueAt).getTime() < now.getTime()) {
    issues.push({ path: 'dueAt', message: REMINDER_MESSAGES.dueAtPast })
  }
  return issues
}
